import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai/index";
import { Tooltip } from "react-tooltip";

import SpeakerSelector from "~/components/settings/SpeakerSelector";
import { PlaySound, playVoice, PlayVoiceParams } from "~/SoundUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import {
  currentSpeakerAtom,
  isProcessingAtom,
  queueArrayAtom,
  summaryQueueArrayAtom,
  T2SEngineAtom,
  voiceIntonationAtom,
  voicePitchAtom,
  voiceSpeedAtom,
  volumeAtom,
} from "~/Atom";
import { talkingModeAtom } from "~/AtomWithDynamoDB";
import * as characterDB from "~/components/db/CharacterDBUtils";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import SliderController from "~/components/utils/SliderController";
import {
  getOpenaiConfig,
  getVoicevoxConfig,
  getAivisSpeechConfig,
} from "~/components/db/CharacterDBUtils";
import "~/css/VoiceSettings.css";
import "~/css/Slider.css";
import { t2SEngines } from "~/Commons";
import { VRM } from "@pixiv/three-vrm";

// スライダーの最小値・最大値
const speedMin = 0.5; // 音声速度の最小値
const speedMax = 2.0; // 音声速度の最大値
const pitchMin = -0.15; // 音声ピッチの最小値
const pitchMax = 0.15; // 音声ピッチの最大値
const intonationMin = 0.0; // 抑揚の最小値
const intonationMax = 2.0; // 抑揚の最大値
const stepValue = 0.01; // スライダーの1ステップの値

// パーセンテージ計算に使用する定数
const percentMultiplier = 100; // パーセンテージ変換用の乗数

// 表示する小数点以下の桁数
const displayDecimalPlaces = 2; // 数値表示の小数点以下の桁数

interface VoiceSettingsProps {
  updateSliderValue: () => void;
  loadedVrm: VRM | null;
  isVoiceReady: boolean;
  isExpression: boolean; // 表情を付けるかのフラグ
}

const VoiceSettings: React.FC<VoiceSettingsProps> = ({
  updateSliderValue,
  loadedVrm,
  isVoiceReady,
  isExpression,
}) => {
  const { t } = useTranslation();
  const [isProcessing] = useAtom(isProcessingAtom);
  const [volume] = useAtom(volumeAtom);
  const [textInput, setTextInput] = useState<string>(t("defaultText"));
  const [, setQueueArray] = useAtom(queueArrayAtom);
  const [speed, setSpeed] = useAtom(voiceSpeedAtom);
  const [pitch, setPitch] = useAtom(voicePitchAtom);
  const [intonation, setIntonation] = useAtom(voiceIntonationAtom);
  const [currentSpeaker] = useAtom(currentSpeakerAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [, setSummaryQueueArray] = useAtom(summaryQueueArrayAtom);
  const [talkingMode] = useAtom(talkingModeAtom); // 0:通常, 1:要約のみ発話

  /**
   * 言語変更時に標準テキストを更新
   */
  useEffect(() => {
    setTextInput(t("defaultText"));
  }, [t]);

  /**
   * テキスト入力の変更を処理
   */
  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setTextInput(event.target.value);
  };

  /**
   * 音声合成実行処理
   * テキストを文単位に分割して再生キューに追加する
   */
  const handleSynthesizeWrapper = async (): Promise<void> => {
    // textInputの文章で、「<」から「>」までを区切って一時退避
    const regex = /<.*?>/g;
    const matches = textInput.match(regex);
    let tempText = textInput;

    if (matches) {
      for (const match of matches) {
        tempText = tempText.replace(match, "");
      }
    }
    if (!isExpression) {
      // [表情]を削除する
      tempText = tempText.replace(/\[.*?\]/g, "");
      // 音声を再生する
      const params: PlayVoiceParams = {
        inputText: tempText,
        T2SEngine,
        currentSpeaker,
        speed,
        pitch,
        intonation,
        volume,
        talkingMode,
      };
      playVoice(params);
    } else {
      // textInputの文章を、「!」,「。」,「？」で区切って配列に格納
      const textArray = tempText.split(/(?<=[！？。])/);
      // 退避していた文章を配列に格納
      if (matches) {
        for (const match of matches) {
          if (match !== "") {
            textArray.push(match);
          }
        }
      }
      // textArrayの末尾に”message_end/0”の要素をアペンド
      textArray.push("message_end/0");
      if (talkingMode === 0) {
        for (const text of textArray) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          setQueueArray((prev) => [...prev, text]);
        }
      } else {
        for (const text of textArray) {
          await new Promise((resolve) => setTimeout(resolve, 0));
          setSummaryQueueArray((prev) => [...prev, text]);
        }
      }
    }
  };

  /**
   * 現在の音声設定をキャラクターDBに保存
   */
  const saveCharacter = async (): Promise<void> => {
    try {
      const name = getVRMName(loadedVrm);
      const voicevox = (await getVoicevoxConfig(name)) || {
        speaker_id: 0,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
      };

      const openai = (await getOpenaiConfig(name)) || {
        speaker_id: 0,
      };

      const aivisSpeech = (await getAivisSpeechConfig(name)) || {
        speaker_id: 0,
        speed: 1.0,
        pitch: 0.0,
        intonation: 1.0,
      };

      switch (T2SEngine) {
        case t2SEngines.voicevox:
          await characterDB.updateByName(
            name,
            "",
            T2SEngine,
            "",
            currentSpeaker.Id,
            speed,
            pitch,
            intonation,
            openai.speaker_id,
            aivisSpeech.speaker_id,
            aivisSpeech.speed,
            aivisSpeech.pitch,
            aivisSpeech.intonation
          );
          break;

        case t2SEngines.openai:
          await characterDB.updateByName(
            name,
            "",
            T2SEngine,
            "",
            voicevox.speaker_id,
            voicevox.speed,
            voicevox.pitch,
            voicevox.intonation,
            currentSpeaker.Id,
            aivisSpeech.speaker_id,
            aivisSpeech.speed,
            aivisSpeech.pitch,
            aivisSpeech.intonation
          );
          break;

        case t2SEngines.aivisSpeech:
          await characterDB.updateByName(
            name,
            "",
            T2SEngine,
            "",
            voicevox.speaker_id,
            voicevox.speed,
            voicevox.pitch,
            voicevox.intonation,
            openai.speaker_id,
            currentSpeaker.Id,
            speed,
            pitch,
            intonation
          );
          break;

        default:
          console.error("Unknown T2SEngine:", T2SEngine);
          return;
      }

      showNotificationDialog(t("saved"), "success");
    } catch (error) {
      console.error("Error saving character settings:", error);
    }
  };

  /**
   * 音声設定をリセット
   */
  const resetCharacter = (): void => {
    updateSliderValue();
  };

  /**
   * 音声速度を変更
   */
  const changeSpeed = (value: number): void => {
    setSpeed(value);
  };

  /**
   * 音声ピッチを変更
   */
  const changePitch = (value: number): void => {
    setPitch(value);
  };

  /**
   * 音声抑揚を変更
   */
  const changeIntonation = (value: number): void => {
    setIntonation(value);
  };

  /**
   * Sliderのdisabled判定
   */
  const isSliderDisabled = T2SEngine === t2SEngines.openai || !isVoiceReady;

  return (
    <>
      {!isVoiceReady && (
        <div className="voice-setting-loading">
          <span>{t("loading")}</span>
        </div>
      )}
      {isVoiceReady && (
        <>
          <div className="menu-items">
            <SpeakerSelector loadedVrm={loadedVrm} />

            <div className="flex-row input-relative">
              <div className="title font-default-normal-20">{t("sampleText")}</div>
              <div className="input-area input-relative font-default-normal-20">
                <textarea
                  value={textInput}
                  onChange={handleTextChange}
                  placeholder={t("enterSpeechContent")}
                  className="input-textbox form-control voice-setting font-default-normal-20"
                />
              </div>
              <button
                data-tooltip-id="tip_play"
                className="btn play-btn"
                onClick={handleSynthesizeWrapper}
                disabled={isProcessing}
                onMouseUp={() => PlaySound("push3", volume)}
              >
                <span className="material-symbols-rounded">send</span>
                <Tooltip
                  id="tip_play"
                  place="bottom"
                  content={t("speechSynthesis")}
                  style={{ zIndex: 1 }}
                />
              </button>
            </div>

            {/* 話速 */}
            <div className="slider">
              <div className="slider-container">
                <span className="slider-title font-default-normal-20">{t("speechSpeed")}</span>
                <div className="slider-content">
                  <input
                    type="range"
                    className="slider-input"
                    min={speedMin}
                    max={speedMax}
                    value={speed}
                    step={stepValue}
                    style={
                      {
                        "--value": `${((speed - speedMin) / (speedMax - speedMin)) * percentMultiplier}%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                    disabled={isSliderDisabled}
                  />
                  <SliderController
                    handleMinusValue={() => changeSpeed(Math.max(speedMin, speed - stepValue))}
                    handlePlusValue={() => changeSpeed(Math.min(speedMax, speed + stepValue))}
                    disabled={isSliderDisabled}
                  />
                  <span className="slider-value">{speed.toFixed(displayDecimalPlaces)}</span>
                </div>
              </div>
            </div>

            {/* 音高 */}
            <div className="slider">
              <div className="slider-container">
                <span className="slider-title font-default-normal-20">{t("pitch")}</span>
                <div className="slider-content">
                  <input
                    type="range"
                    className="slider-input"
                    min={pitchMin}
                    max={pitchMax}
                    value={pitch}
                    step={stepValue}
                    style={
                      {
                        "--value": `${((pitch - pitchMin) / (pitchMax - pitchMin)) * percentMultiplier}%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) => changePitch(parseFloat(e.target.value))}
                    disabled={isSliderDisabled}
                  />
                  <SliderController
                    handleMinusValue={() => changePitch(Math.max(pitchMin, pitch - stepValue))}
                    handlePlusValue={() => changePitch(Math.min(pitchMax, pitch + stepValue))}
                    disabled={isSliderDisabled}
                  />
                  <span className="slider-value">{pitch.toFixed(displayDecimalPlaces)}</span>
                </div>
              </div>
            </div>

            {/* 抑揚 */}
            <div className="slider">
              <div className="slider-container">
                <span className="slider-title font-default-normal-20">{t("intonation")}</span>
                <div className="slider-content">
                  <input
                    type="range"
                    className="slider-input"
                    min={intonationMin}
                    max={intonationMax}
                    value={intonation}
                    step={stepValue}
                    style={
                      {
                        "--value": `${((intonation - intonationMin) / (intonationMax - intonationMin)) * percentMultiplier}%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) => changeIntonation(parseFloat(e.target.value))}
                    disabled={isSliderDisabled}
                  />
                  <SliderController
                    handleMinusValue={() =>
                      changeIntonation(Math.max(intonationMin, intonation - stepValue))
                    }
                    handlePlusValue={() =>
                      changeIntonation(Math.min(intonationMax, intonation + stepValue))
                    }
                    disabled={isSliderDisabled}
                  />
                  <span className="slider-value">{intonation.toFixed(displayDecimalPlaces)}</span>
                </div>
              </div>
            </div>

            <div className="flex-row voice-setting-margin">
              <button
                data-tooltip-id="tip_reset"
                className="button-0"
                onClick={resetCharacter}
                onMouseUp={() => PlaySound("cancel", volume)}
                disabled={isProcessing}
              >
                {t("reset")}
              </button>
              <button
                data-tooltip-id="tip_save"
                className="button-1"
                onClick={saveCharacter}
                onMouseUp={() => PlaySound("push3", volume)}
                disabled={isProcessing}
              >
                {t("save")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default VoiceSettings;
